import React, { useState, useCallback, useMemo } from 'react';
import { Button, Modal, ReceiptPreview } from './';
import { api } from '../_library';

import {
  TransactionReceipt,
  getReceiptStatus,
  canUploadForStatus,
  canApproveForStatus,
  type ReceiptStatus,
} from './TransactionReceipt';

type Props = {
  id: string | number;
  transactionApproved?: boolean;
  receiptStatus: ReceiptStatus;
  receiptAllowed: boolean;
  onChange?: () => void;
};

export const ReceiptCell: (props: Props) => JSX.Element | null = ({
    id,
    transactionApproved = false,
    receiptStatus,
    receiptAllowed,
    onChange,
}) => {
    const [open, setOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [approved, setApproved] = useState(transactionApproved);
    const [localReceiptStatus, setLocalReceiptStatus] = useState(receiptStatus);

    // a file only exists once something has been uploaded; 'add' means none yet
    const hasReceipt = useMemo(
        () => receiptAllowed && localReceiptStatus !== 'add',
        [receiptAllowed, localReceiptStatus]
    );

    const ui = useMemo(() => getReceiptStatus(localReceiptStatus, receiptAllowed), [localReceiptStatus, receiptAllowed]);

    const allowUpload = useMemo(
        () => canUploadForStatus(localReceiptStatus, receiptAllowed),
        [localReceiptStatus, receiptAllowed]
    );
    const allowApprove   = useMemo(
        () => !approved && canApproveForStatus(localReceiptStatus, receiptAllowed),
        [approved, localReceiptStatus, receiptAllowed]
    );
    const allowUnapprove = useMemo(
        () =>  approved,
        [approved]
    );

    const approve = useCallback(async () => {
        setBusy(true);
        try {
            const res = await api.post(`/transactions/${id}/approve`, {});
            setApproved(res.data.transactionApproved);
            onChange?.();
        } finally {
            setBusy(false);
        }
    }, [id, onChange]);

    const unapprove = useCallback(async () => {
        setBusy(true);
        try {
            const res = await api.post(`/transactions/${id}/unapprove`, {});
            setApproved(res.data.transactionApproved);
            onChange?.();
        } finally {
            setBusy(false);
        }
    }, [id, onChange]);

    if (!ui) return null;

    return (
        <>
            <div className="flex items-center justify-center space-x-2">
                {/* icons inline */}
                <div className="flex space-x-1">
                    {ui.icons.map((src: string, i: number) => (
                    <img key={i} alt="icon" src={src} className="w-5 h-5" />
                ))}
                </div>

                {/* label next to icons */}
                {/* <Text label={ui.text} /> */}

                {allowApprove ?
                    <Button
                        extraSmall
                        disabled={busy}
                        onClick={approve}
                        label="bk.transaction_receipt.btn.approve"
                    /> : null
                }

                {allowUnapprove ?
                    <Button
                        extraSmall
                        secondary
                        disabled={busy}
                        onClick={unapprove}
                        label="bk.transaction_receipt.btn.unapprove"
                    /> : null
                }

                {hasReceipt ?
                    <Button
                        extraSmall
                        color="grey"
                        onClick={() => setPreviewOpen(true)}
                        label="bk.transaction_receipt.btn.view"
                    /> : null
                }

                {allowUpload ?
                    <Button
                        extraSmall
                        color="grey"
                        onClick={() => setOpen(true)}
                        label="bk.transaction_receipt.btn.upload"
                    /> : null
                }
            </div>

            {previewOpen ?
                <Modal
                    isOpen
                    toggle={() => setPreviewOpen(false)}
                    title="bk.transaction_receipt.preview.title"
                    content={<ReceiptPreview transactionId={id} />}
                /> : null
            }

            {open ? 
                <Modal
                isOpen
                toggle={() => setOpen(false)}
                title="bk.transaction_receipt.title"
                content={
                    <TransactionReceipt
                    transaction={{
                        id,
                        receiptStatus,
                        receiptAllowed,
                    }}
                    onUploadComplete={() => {
                        setLocalReceiptStatus('processing');
                        setOpen(false);
                        onChange?.();
                    }}
                    />
                }
                /> : null
            }
        </>
    );
};
